import React, { createContext, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Profile } from "../assets/models/profile";
import {
  clearStudentLearningProgress,
  practiceWords,
} from "../services/backend/api";
import type { Word } from "../services/backend/backendTypes";
import {
  clearTeacherPracticeSession,
  readTeacherPracticeSession,
} from "../services/teacherPracticeSession";

type ContextType = {
  profiles: Profile[];
  selectedProfile: any;
  handleProfileSelect: (
    profile: any,
    options?: { navigateToProfile?: boolean }
  ) => Promise<void>;
  logout: () => void;
  words: Word[];
  currentWord: Word | null;
  getOrUpdateToNextWord: () => Promise<Word | undefined>;
};

const ProfileContext = createContext<ContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [profiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [words, setWords] = useState<Word[]>([]);
  const [selectedWordIdx, setSelectedWordIdx] = useState<number>(0);
  const router = useNavigate();
  const location = useLocation();

  const handleFetchWords = async (studentId: number) => {
    const wordData = await practiceWords(studentId);
    if (wordData) {
      setWords(wordData);
      setSelectedWordIdx(0);
    }

    console.log("Fetched words:", wordData);

    return wordData;
  };

  const handleProfileSelect = async (
    selected: any,
    options?: { navigateToProfile?: boolean }
  ) => {
    setSelectedProfile(selected);

    const studentId = selected.studentId && selected.studentId > 0
      ? selected.studentId
      : -1;

    if (studentId <= 0) {
      await clearStudentLearningProgress(studentId);
    }

    await handleFetchWords(studentId);

    if (options?.navigateToProfile !== false) {
      router("/profile");
    }
  };

  const logout = () => {
    localStorage.removeItem("activeTeacher");
    clearTeacherPracticeSession();
    setSelectedProfile(null);
    setWords([]);
    setSelectedWordIdx(0);
    router("/", { replace: true });
  };

  const getOrUpdateToNextWord = async () => {
    if (!selectedProfile?.studentId) {
      return undefined;
    }

    const nextIndex = selectedWordIdx + 1;

    if (nextIndex >= words.length) {
      const refreshedWords = await handleFetchWords(selectedProfile.studentId);
      return refreshedWords?.[0];
    }

    setSelectedWordIdx(nextIndex);
    return words[nextIndex];
  };

  // Redirect to login if not signed in (except for login / teacher pages)
  React.useEffect(() => {
    const path = location.pathname;
    if (path === "/" || path.startsWith("/login")) {
      return;
    }

    const hasActiveTeacher = Boolean(localStorage.getItem("activeTeacher"));
    if (path === "/teacher-dashboard") {
      if (!hasActiveTeacher) {
        router("/", { replace: true });
      }
      return;
    }

    if (path === "/word-prompt" && hasActiveTeacher && readTeacherPracticeSession()) {
      return;
    }

    if (!selectedProfile) {
      router("/", { replace: true });
    }
  }, [location.pathname, router, selectedProfile]);

  return (
    <ProfileContext.Provider
      value={{
        selectedProfile,
        handleProfileSelect,
        getOrUpdateToNextWord,
        logout,
        words,
        profiles: profiles as Profile[],
        currentWord: words[selectedWordIdx] || null,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  return context;
};
